'use client';

import {
  PageHeaderActionsSlot,
  PageHeaderAlertSlot,
  PageHeaderBreadcrumbSlot,
  PageHeaderDescriptionSlot,
  PageHeaderTitleBadgeSlot,
  PageHeaderTitleSlot,
} from '@magicschool/spellbook/components/PageHeader/slots';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import { cva } from 'class-variance-authority';
import { type ComponentProps, type FC, useEffect } from 'react';

export type BreadcrumbData = {
  element?: React.ElementType;
  type: 'link' | 'page';
  label: string;
  href?: string;
  /** Runs when the crumb is clicked, in the row or from the collapsed-crumbs menu. */
  onClick?: React.MouseEventHandler<HTMLElement>;
};

export type PageHeaderProps = ComponentProps<'div'> & {
  /** Breadcrumb trail entries. When provided, auto-renders the Breadcrumb slot. Last entry should be `type: 'page'`. */
  breadcrumbs?: BreadcrumbData[];
  /** Adds a bottom border to visually separate the header from page content below. */
  withBorder?: boolean;
};

const pageHeaderVariants = cva('w-full max-w-[96rem] mx-auto flex flex-wrap border-border-default px-4 sm:px-6 pt-3 gap-x-4', {
  variants: {
    standalone: {
      true: 'items-center',
    },
    withBorder: {
      true: 'border-b',
    },
  },
});

const PageHeaderBase: FC<PageHeaderProps> = ({ breadcrumbs, children, className, withBorder = false, ...props }) => {
  const { slots } = useSlots(children, {
    breadcrumb: { Component: PageHeaderBreadcrumbSlot, props: { breadcrumbs } },
    title: { Component: PageHeaderTitleSlot },
    titleBadge: { Component: PageHeaderTitleBadgeSlot },
    description: { Component: PageHeaderDescriptionSlot },
    actions: { Component: PageHeaderActionsSlot },
    alert: { Component: PageHeaderAlertSlot },
  });

  useEffect(() => {
    if (!slots.title) {
      console.warn('PageHeader requires a Title slot.');
    }
  }, [slots.title]);

  return (
    <div
      data-spellbook-name="page-header"
      className={cn(pageHeaderVariants({ standalone: !breadcrumbs?.length && !slots.description && !slots.tabs, withBorder }), className)}
      data-with-border={withBorder}
      {...props}
    >
      {slots.alert}
      <div className="mt-2 flex-1">
        <div className="flex flex-col gap-1 mb-3">
          {slots.breadcrumb}
          {slots.titleBadge ? (
            <div className="flex items-center gap-2">
              {slots.title}
              {slots.titleBadge}
            </div>
          ) : (
            <>{slots.title}</>
          )}
          {slots.description}
        </div>
      </div>
      {slots.actions}
    </div>
  );
};

export const PageHeader = Object.assign(PageHeaderBase, {
  Breadcrumb: PageHeaderBreadcrumbSlot,
  Title: PageHeaderTitleSlot,
  TitleBadge: PageHeaderTitleBadgeSlot,
  Description: PageHeaderDescriptionSlot,
  Actions: PageHeaderActionsSlot,
  Alert: PageHeaderAlertSlot,
});

/**
 * @TODO [SPE-8] Fix `displayName` assignment
 * https://linear.app/magicschool/issue/SPE-8/fix-displayname-assignment
 */
PageHeader.displayName = 'PageHeader';
PageHeader.Breadcrumb.displayName = 'PageHeader.Breadcrumb';
PageHeader.Title.displayName = 'PageHeader.Title';
PageHeader.TitleBadge.displayName = 'PageHeader.TitleBadge';
PageHeader.Description.displayName = 'PageHeader.Description';
PageHeader.Actions.displayName = 'PageHeader.Actions';
PageHeader.Alert.displayName = 'PageHeader.Alert';
